/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import {
  Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import { TranslationService } from '../../common/translation.service';
import { generateObjectId } from '../../app/exports';

/**
 * Component for our aero-input field.
 */
@Component({
  selector: 'aero-input',
  templateUrl: './aero-input.component.html',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AeroInputComponent),
    multi: true,
  }],
  standalone: false,
})
export class AeroInputComponent implements ControlValueAccessor, OnChanges, OnInit {
  /**
   * Reference to the DOM input element.
   */
  @ViewChild('input') public aeroInputNgModel : NgModel;

  /**
   * Emit the current ngModel FormControl.
   */
  @Output() public aeroInputNgModelEmitter : EventEmitter< any > = new EventEmitter< any >();

  /**
   * Input type.
   */
  @Input() public type : string = 'text';

  /**
   * Set the value as true for the email input field.
   */
  @Input() public email? : any;

  /**
   * To set the maxlength of the input field.
   */
  @Input() public maxlength? : any;

  /**
   * To set the minlength of the input field.
   */
  @Input() public minlength? : any;

  /**
   * To set the pattern of the input field.
   */
  @Input() public pattern? : any;

  /**
   * To set the attribute as true to make the input field is required.
   */
  @Input() public required? : boolean = false;

  /**
   * To set the placeholder for the input field.
   */
  @Input() public placeholder? : string = '';

  /**
   * Id of the input field.
   */
  @Input() public id? : string = '';

  /**
   * Name of the input field.
   */
  @Input() public name? : string = '';

  /**
   * Label for the input field.
   */
  @Input() public label : string = '';

  /**
   * To set the value as true to disable the input field.
   */
  @Input() public disabled? : boolean = false;

  /**
   * To set the value as true to make the input field as readOnly.
   */
  @Input() public readonly? : boolean = false;

  /**
   * Apply autofocus to input using bulbAutofocus directive.
   */
  @Input() public autofocus: boolean = false;

  /**
   * Autocomplete behavior for the input field.
   */
  @Input() public autocomplete? : string;

  /**
   * Autocorrect behavior for the input field.
   */
  @Input() public autocorrect? : string;

  /**
   * Set the error message for the input field.
   */
  @Input() public errorMessage : string = '';

  /**
   * Set the hint message for the input field.
   */
  @Input() public hintMessage? : string = '';

  /**
   * To set the errors for the input field.
   */
  @Input() public errors: any = null;

  /**
   * To set the aria label for the input field.
   */
  @Input() public ariaLabel : string;

  /**
   * To set the value for the input field.
   */
  private value : string;

  /**
   * Default Constructor.
   */
  constructor(private $translate : TranslationService) {}

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit(): void {
    // Translate the default error message
    this.errorMessage = this.errorMessage || this.$translate.translateInstant('validation_error_required');
    // Each input field needs to be unique, or accessibility will explode!
    this.id = (this.id) ? this.id : generateObjectId();
  }

  /**
   * A lifecycle hook that is called when any data-bound property of a component changes.
   *
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.errors && this.aeroInputNgModel) {
      // To set the errors of the input field.
      this.aeroInputNgModel.control.setErrors(changes.errors.currentValue);
    }
  }

  /**
   * Get the value of the input.
   */
  get inputValue(): any {
    return this.value;
  }

  /**
   * Set the value of the input.
   *
   * @param newValue of the input field.
   */
  set inputValue(newValue: any) {
    if (this.value !== newValue) {
      this.value = newValue;
      this.onChange(newValue);
    }
  }

  /**
   * Callback function to register the UI value changes.
   */
  onChange: any = () => { };

  /**
   * Callback function to register on element touch.
   */
  onTouched: any = () => { };

  /**
   * Set the touched on the blur event.
   */
  onBlur() {
    this.onTouched();
  }

  /**
   * Method to set the value.
   *
   * @param updateValue of the input field.
   */
  writeValue(updateValue: any) {
    if (updateValue !== this.value) {
      this.value = updateValue;
    }
  }

  /**
   * Method gets triggered when the UI element value changes.
   *
   * @param updateValue of the input field.
   */
  registerOnChange(updateValue: any) {
    this.onChange = updateValue;
  }

  /**
   * Method gets triggered when the UI element value changes.
   *
   * @param updateValue of the input field.
   */
  registerOnTouched(updateValue: any) {
    this.onTouched = updateValue;
  }

  /**
   * Method that is invoked on an update of a model.
   */
  updateChanges() {
    this.aeroInputNgModelEmitter.emit(this.aeroInputNgModel);
    this.onChange(this.value);
  }

  /**
   * Method called by the parent component to reset this field.
   */
  resetField() {
    const fieldControl = this.aeroInputNgModel.control;
    fieldControl.setErrors(null);
    fieldControl.markAsPristine();
    fieldControl.markAsUntouched();
  }
}
